import {
  AsYouType,
  type CountryCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min'
import {
  PHONE_COUNTRIES,
  type PhoneCountry,
  countryOnDialCode,
  leadingDialCode,
  nationalPart,
  phoneCountry,
} from './phoneCountries'

export function formatPhoneDraft(country: PhoneCountry, value: string): string {
  const national = nationalPart(value)
  if (country.dialCode === '+1') {
    // A pasted number keeps its own punctuation past the dial code, so the
    // groups are cut from the digits rather than the raw characters.
    const digits = national.replace(/\D/g, '')
    return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6)]
      .filter(Boolean)
      .join('-')
  }
  if (!isSupportedCountry(country.alpha2 as CountryCode)) {
    return national
  }
  return new AsYouType(country.alpha2 as CountryCode).input(national)
}

/** Removing a formatting character must still let Backspace remove a digit. */
export function editPhoneDraft(previous: string, next: string): string {
  const digits = next.replace(/\D/g, '')
  if (
    next.length >= previous.length ||
    digits !== previous.replace(/\D/g, '')
  ) {
    return next
  }
  // Backspace landed on a separator, so take the digit the caret sat behind
  // rather than the last one typed.
  const leading = previous
    .slice(0, divergedAt(previous, next))
    .replace(/\D/g, '')
  if (leading.length === 0) {
    return digits
  }
  return leading.slice(0, -1) + digits.slice(leading.length)
}

const divergedAt = (previous: string, next: string): number => {
  for (let at = 0; at < next.length; at += 1) {
    if (next[at] !== previous[at]) {
      return at
    }
  }
  return next.length
}

/** A number split where its calling code ends. */
export interface DialledPhone {
  country: PhoneCountry
  national: string
}

/** A pasted international number moves its prefix into the country selector. */
export function pastedPhone(value: string): DialledPhone | undefined {
  if (!value.trimStart().startsWith('+')) {
    return undefined
  }
  const parsed = parsePhoneNumberFromString(value)
  if (parsed === undefined || !parsed.isValid()) {
    return undefined
  }
  const country = PHONE_COUNTRIES.find((item) => item.alpha2 === parsed.country)
  if (country === undefined) {
    return undefined
  }
  return { country, national: parsed.nationalNumber }
}

/**
 * A number arriving whole, with the code it carries moved to the chip:
 * `pastedPhone` moves one only out of a number libphonenumber calls valid.
 */
export function insertedPhone(
  previous: string,
  next: string,
  selected: PhoneCountry,
): DialledPhone | undefined {
  const parsed = pastedPhone(next)
  if (parsed !== undefined) {
    return parsed
  }
  // One character more is a keystroke: `+4` on the way to `+44` is not yet a
  // code, and a number the platform fills in arrives whole.
  if (next.length - previous.length < 2) {
    return undefined
  }
  return dialledInsert(next, selected) ?? nanpInsert(next, selected)
}

/** `+44…`, and the `0044…` the rest of the world writes it as. */
function dialledInsert(
  value: string,
  selected: PhoneCountry,
): DialledPhone | undefined {
  const digits = internationalDigits(value)
  if (digits === undefined) {
    return undefined
  }
  const code = leadingDialCode(digits)
  if (code === undefined) {
    return undefined
  }
  return {
    country: countryOnDialCode(code, namedCountry(digits) ?? selected),
    national: digits.slice(code.length - 1),
  }
}

/** The digits behind an international prefix, where the value carries one. */
function internationalDigits(value: string): string | undefined {
  const trimmed = value.trimStart()
  const digits = trimmed.replace(/\D/g, '')
  if (trimmed.startsWith('+')) {
    return digits
  }
  return trimmed.startsWith('00') ? digits.slice(2) : undefined
}

/** Which of the countries on a code the number itself reads as, if any. */
function namedCountry(digits: string): PhoneCountry | undefined {
  return phoneCountry(parsePhoneNumberFromString(`+${digits}`)?.country)
}

/** Ten digits are a whole NANP number, so an eleventh behind `1` is the code. */
function nanpInsert(
  value: string,
  selected: PhoneCountry,
): DialledPhone | undefined {
  const digits = value.replace(/\D/g, '')
  if (
    selected.dialCode !== NANP_DIAL_CODE ||
    !digits.startsWith(NANP_TRUNK_PREFIX) ||
    digits.length <= NANP_NATIONAL_LENGTH
  ) {
    return undefined
  }
  return { country: selected, national: digits.slice(NANP_TRUNK_PREFIX.length) }
}

const NANP_DIAL_CODE = '+1'
const NANP_TRUNK_PREFIX = '1'
/** Area code and subscriber number, which is the whole of a NANP number. */
const NANP_NATIONAL_LENGTH = 10
